import { Button, AnimateBounceRight } from '@dreamstack/feature-components/src/layout/Button'
import { GridWithGaps } from '@dreamstack/feature-components/src/layout/MainGrid'
import type { Option } from '@dreamstack/feature-components/src/property/GroupedAutocomplete'
import { GroupedAutocomplete } from '@dreamstack/feature-components/src/property/GroupedAutocomplete'
import { ArrowRight } from '@dreamstack/icons/src/icon-components/ArrowRight'
import upperFirst from 'lodash/upperFirst'
import type { FunctionComponent } from 'react'
import * as React from 'react'
import { useEffect, useMemo, useState } from 'react'

type RegionCityLocation = {
  name: string
  type: string
  count: number
}

export type RegionCityLocationOption = Option<RegionCityLocation>

const PROPERTIES_EN_BASE_PATH = 'https://accentro.de/en/properties?'

export const getKey = (option: RegionCityLocationOption | string) => {
  if (typeof option === 'string') {
    return 'cities'
  } else {
    switch (option.value.type) {
      case 'location':
        return 'locations'
      case 'region':
        return 'regions'
      default:
        return 'cities'
    }
  }
}

export const getValue = (
  option: RegionCityLocationOption | string
): string | undefined => {
  return typeof option === 'string' ? option : option.value.name ?? undefined
}

const buildQueryString = (
  selectedOptions: (RegionCityLocationOption | string)[]
): string => {
  const searchParams = new URLSearchParams()
  for (const option of selectedOptions) {
    const value = getValue(option)
    if (value !== undefined) {
      searchParams.append(getKey(option), value)
    }
  }

  return searchParams.toString()
}

const useFetchRegionsCitiesLocations = () => {
  const [autocompleteCities, setAutocompleteCities] = useState<
    RegionCityLocation[]
  >([])
  useEffect(() => {
    const fetchRegionsCitiesLocations = async () => {
      try {
        const response = await fetch('/api/regions-cities-locations')
        if (!response.ok) {
          return
        }
        const payload = await response.json()
        if (Array.isArray(payload)) {
          setAutocompleteCities(payload)
        }
      } catch (error) {
        console.error('Failed to load regions, cities and locations', error)
      }
    }
    fetchRegionsCitiesLocations()
  }, [])
  return autocompleteCities
}

export const RegionCityLocationAutocomplete = ({
  placeholder,
  selectedOptions,
  setSelectedOptions,
}: {
  placeholder: string
  selectedOptions: (RegionCityLocationOption | string)[]
  setSelectedOptions: (options: (RegionCityLocationOption | string)[]) => void
}) => {
  const [options, setOptions] = useState<RegionCityLocationOption[]>([])
  const autocompleteCities = useFetchRegionsCitiesLocations()

  useEffect(() => {
    if (autocompleteCities) {
      const options: RegionCityLocationOption[] = autocompleteCities
        .map((r) => ({
          value: r,
          optionLabel: r.name ?? '',
          optionDisplayedQuantity: r.count,
          optionGroupLabel: upperFirst(r?.type ?? ''),
        }))
        .sort((optionA, optionB) => {
          const groupComparison = optionB.optionGroupLabel.localeCompare(
            optionA.optionGroupLabel
          )

          if (groupComparison !== 0) {
            return groupComparison
          }

          return optionA.optionLabel.localeCompare(optionB.optionLabel)
        })
      setOptions(options)
    }
  }, [autocompleteCities])

  return (
    <GroupedAutocomplete
      placeholder={placeholder}
      options={options}
      selectedOptions={selectedOptions}
      setSelectedOptions={setSelectedOptions}
      uiVariant="filled"
    />
  )
}

export const FindYourProperties: FunctionComponent<
  React.PropsWithChildren<{}>
> = () => {
  const buttonLabel = 'Find your ideal property'

  const [selectedOptions, setSelectedOptions] = React.useState<
    (RegionCityLocationOption | string)[]
  >([])

  const link = useMemo(() => {
    return PROPERTIES_EN_BASE_PATH + buildQueryString(selectedOptions)
  }, [selectedOptions])

  return (
    <GridWithGaps className="text-accentroWhite-full!">
      <div className="col-span-full">
        <div className="flex flex-col w-full md:flex-row md:items-center justify-center">
          <div className="mb-px-16 md:mb-0 md:mr-px-48 flex-1">
            <RegionCityLocationAutocomplete
              placeholder="Where do you want to invest?"
              selectedOptions={selectedOptions}
              setSelectedOptions={setSelectedOptions}
            />
          </div>
          <div>
            {/* <Link href={link} target="_blank"> */}
            <Button
              href={link}
              variant="contained"
              color="primary"
              endIcon={
                <AnimateBounceRight>
                  <ArrowRight />
                </AnimateBounceRight>
              }
            >
              {buttonLabel}
            </Button>
            {/* </Link> */}
          </div>
        </div>
      </div>
    </GridWithGaps>
  )
}
